/** Cùng padding với `useThemePreview` (fd-selected-overlay). */
export const SELECTED_OVERLAY_PADDING = 3;

/** Viewport DOMRect → toạ độ local trong shadow host (cho overlay `position: absolute`). */
export function viewportRectToHostLocal(
  rect: DOMRect,
  host: HTMLElement | null | undefined,
): DOMRect {
  if (!host) return rect;
  const hr = host.getBoundingClientRect();
  return new DOMRect(
    rect.left - hr.left,
    rect.top - hr.top,
    rect.width,
    rect.height,
  );
}

export function humanizeDataCompLabel(input: string): string {
  if (!input) return "";
  let s = input
    .replace(/[_-]+/g, " ")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return s
    .split(" ")
    .map((word) => {
      if (word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Một số template gắn trùng `data-comp` (vd. Saola: wrapper bảng + `paid-status` đều là LineItem).
 * Khi union rect của cả tập, bbox lệch so với khối người dùng thấy — chỉ giữ node không bị
 * node khác trong tập bao bọc (ancestor cùng danh sách).
 */
export function filterToOutermostElements(elements: HTMLElement[]): HTMLElement[] {
  return elements.filter((el) => !elements.some((other) => other !== el && other.contains(el)));
}

export function unionBoundingRect(elements: HTMLElement[]): DOMRect | null {
  if (!elements.length) return null;
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const el of elements) {
    const r = el.getBoundingClientRect();
    minL = Math.min(minL, r.left);
    minT = Math.min(minT, r.top);
    maxR = Math.max(maxR, r.right);
    maxB = Math.max(maxB, r.bottom);
  }
  if (minL === Infinity) return null;
  return new DOMRect(minL, minT, maxR - minL, maxB - minT);
}

export function getScrollableAncestors(
  el: Element | null,
  shadowRoot: ShadowRoot,
  win: Window
): (Element | Window)[] {
  const result: (Element | Window)[] = [];
  if (!el) return result;
  let node: Element | null = el.parentElement;
  while (node && node !== shadowRoot.host) {
    try {
      const style = win.getComputedStyle(node);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;
      const canScrollY =
        (overflowY === "auto" || overflowY === "scroll") &&
        node.scrollHeight > node.clientHeight;
      const canScrollX =
        (overflowX === "auto" || overflowX === "scroll") &&
        node.scrollWidth > node.clientWidth;
      if (canScrollY || canScrollX) result.push(node);
    } catch {
      /* noop */
    }
    node = node.parentElement;
  }
  result.push(win);
  return result;
}

export function updateOverlayBox(overlay: HTMLDivElement, rect: DOMRect, padding: number) {
  const left = `${Math.round(rect.left - padding)}px`;
  const top = `${Math.round(rect.top - padding)}px`;
  const width = `${Math.round(rect.width + padding * 2)}px`;
  const height = `${Math.round(rect.height + padding * 2)}px`;

  if (overlay.style.display !== "block") overlay.style.display = "block";
  if (overlay.style.left !== left) overlay.style.left = left;
  if (overlay.style.top !== top) overlay.style.top = top;
  if (overlay.style.width !== width) overlay.style.width = width;
  if (overlay.style.height !== height) overlay.style.height = height;
}

function updateTagLayout(tagEl: HTMLDivElement, mustPushDown: boolean) {
  const nextTransform = mustPushDown ? "translate(-2px, 0)" : "translate(-2px, -100%)";
  const nextRadius = mustPushDown ? "0 0 6px 6px" : "6px 6px 0 0";
  if (tagEl.style.transform !== nextTransform) tagEl.style.transform = nextTransform;
  if (tagEl.style.borderRadius !== nextRadius) tagEl.style.borderRadius = nextRadius;
}

function updateTagText(tagEl: HTMLDivElement, label: string) {
  if (tagEl.textContent !== label) {
    tagEl.textContent = label;
  }
}

/**
 * Cập nhật overlay + tag (fd-selected / sidebar-nav) từ rect + chuỗi data-comp.
 */
export function updateSelectedOverlayFromRect(
  overlay: HTMLDivElement,
  tagEl: HTMLDivElement | null,
  rect: DOMRect,
  dataCompRaw: string,
  padding = SELECTED_OVERLAY_PADDING,
  host?: HTMLElement | null,
) {
  const localRect = viewportRectToHostLocal(rect, host);
  updateOverlayBox(overlay, localRect, padding);

  if (!tagEl) return;
  const parts = dataCompRaw.split(".").filter(Boolean);
  const label =
    parts.length > 0
      ? parts.map((p) => humanizeDataCompLabel(p)).join(" → ")
      : humanizeDataCompLabel(dataCompRaw);
  // mustPushDown: dựa viewport top (tag gần mép trên viewport/modal)
  const mustPushDown = rect.top - padding <= 18;
  updateTagText(tagEl, label);
  updateTagLayout(tagEl, mustPushDown);
}
